/**
 * Chess960x32 shared chess engine.
 *
 * Pure, framework-free rules code used by BOTH runtimes:
 *  - the React client (src/hooks/useChessGame.ts delegates to it)
 *  - the Deno server (server/main.ts imports it to validate snapshots)
 *
 * Nothing in this file may touch the DOM, Node, or Deno APIs. Imports use
 * explicit `.ts` extensions because Deno requires them; Vite and `tsc`
 * resolve them fine (allowImportingTsExtensions is enabled).
 */
import {
    Board,
    BoardSquare,
    CastlingRights,
    Color,
    GameMove,
    GameState,
    GameVariation,
    OnlineGameSnapshot,
    OnlineRoomConfig,
    Piece,
    PieceType,
    PIECE_SYMBOLS,
    PlayerPieces,
    Position,
    PromotionRule,
} from '../types.ts';
import { cloneBoard, isValidSquare, positionToAlgebraic } from '../utils/boardUtils.ts';
import { chess960PositionFromId } from '../utils/chess960Utils.ts';

// ─── Public constants ────────────────────────────────────────────────────────

export const PIECE_NAMES: Record<PieceType, string> = {
    K: 'King',
    Q: 'Queen',
    R: 'Rook',
    B: 'Bishop',
    N: 'Knight',
    P: 'Pawn',
    H: 'Hawk',
    E: 'Elephant',
    A: 'Amazon'
};

export const COLOR_NAMES: Record<Color, string> = {
    white: 'White',
    black: 'Black'
};

export const VALID_PIECE_TYPES: PieceType[] = ['K', 'Q', 'R', 'B', 'N', 'P', 'H', 'E', 'A'];
export const VALID_COLORS: Color[] = ['white', 'black'];
export const VALID_VARIATIONS: GameVariation[] = [
    'chess', 'chessx2', 'chessx8', 'chessx32',
    'chess960', 'chess960x2', 'chess960x8', 'chess960x32',
    'chess960blind'
];
export const VALID_PROMOTION_RULES: PromotionRule[] = ['limited', 'full'];

// ─── Position context ────────────────────────────────────────────────────────

export interface KingPositions {
    white: Position | null;
    black: Position | null;
}

export interface RookPositions {
    whiteKingSide: Position | null;
    whiteQueenSide: Position | null;
    blackKingSide: Position | null;
    blackQueenSide: Position | null;
}

export interface EnginePosition {
    board: Board;
    gameVariation: GameVariation;
    enPassantTarget: Position | null;
    castlingRights: CastlingRights;
    kingPositions: KingPositions;
    rookPositions: RookPositions;
}

export interface MoveApplicationResult {
    board: Board;
    castlingRights: CastlingRights;
    kingPositions: KingPositions;
    rookPositions: RookPositions;
    enPassantTarget: Position | null;
}

export const createEmptyBoard = (): Board =>
    Array(8).fill(null).map(() => Array(8).fill(null));

export const createEmptyRookPositions = (): RookPositions => ({
    whiteKingSide: null,
    whiteQueenSide: null,
    blackKingSide: null,
    blackQueenSide: null
});

export const createEmptyKingPositions = (): KingPositions => ({
    white: null,
    black: null
});

export const createEmptyCastlingRights = (): CastlingRights => ({
    whiteKingSide: true,
    whiteQueenSide: true,
    blackKingSide: true,
    blackQueenSide: true
});

export const createEmptyEnginePosition = (gameVariation: GameVariation = 'chess'): EnginePosition => ({
    board: createEmptyBoard(),
    gameVariation,
    enPassantTarget: null,
    castlingRights: createEmptyCastlingRights(),
    kingPositions: createEmptyKingPositions(),
    rookPositions: createEmptyRookPositions()
});

export const positionFromSnapshot = (snapshot: OnlineGameSnapshot): EnginePosition => ({
    board: cloneBoard(snapshot.board),
    gameVariation: snapshot.gameVariation,
    enPassantTarget: snapshot.enPassantTarget,
    castlingRights: { ...snapshot.castlingRights },
    kingPositions: { ...snapshot.kingPositions },
    rookPositions: { ...snapshot.rookPositions }
});

// ─── Movement primitives ─────────────────────────────────────────────────────

const ROOK_DIRECTIONS: Position[] = [[0, 1], [0, -1], [1, 0], [-1, 0]];
const BISHOP_DIRECTIONS: Position[] = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
const QUEEN_DIRECTIONS: Position[] = [...ROOK_DIRECTIONS, ...BISHOP_DIRECTIONS];
const KING_DIRECTIONS: Position[] = [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]];
const KNIGHT_MOVES: Position[] = [[-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1]];

export const findKingPosition = (board: Board, color: Color): Position | null => {
    for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
            const piece = board[row][col];
            if (piece && piece.piece === 'K' && piece.color === color) {
                return [row, col];
            }
        }
    }
    return null;
};

export const isSquareUnderAttack = (board: Board, row: number, col: number, byColor: Color): boolean => {
    const hasEnemyAt = (r: number, c: number, types: PieceType[]): boolean => {
        if (!isValidSquare(r, c)) return false;
        const p = board[r][c];
        return p !== null && p.color === byColor && types.includes(p.piece);
    };

    // Sliding attacks along orthogonal directions (Rook, Queen, Amazon, Elephant)
    for (const [dr, dc] of ROOK_DIRECTIONS) {
        for (let r = row + dr, c = col + dc; isValidSquare(r, c); r += dr, c += dc) {
            const p = board[r][c];
            if (p) {
                if (p.color === byColor && (p.piece === 'R' || p.piece === 'Q' || p.piece === 'A' || p.piece === 'E')) return true;
                break;
            }
        }
    }

    // Sliding attacks along diagonal directions (Bishop, Queen, Amazon, Hawk)
    for (const [dr, dc] of BISHOP_DIRECTIONS) {
        for (let r = row + dr, c = col + dc; isValidSquare(r, c); r += dr, c += dc) {
            const p = board[r][c];
            if (p) {
                if (p.color === byColor && (p.piece === 'B' || p.piece === 'Q' || p.piece === 'A' || p.piece === 'H')) return true;
                break;
            }
        }
    }

    // Knight jumps (Knight, Hawk, Elephant, Amazon)
    for (const [dr, dc] of KNIGHT_MOVES) {
        if (hasEnemyAt(row + dr, col + dc, ['N', 'H', 'E', 'A'])) return true;
    }

    // King adjacency
    for (const [dr, dc] of KING_DIRECTIONS) {
        if (hasEnemyAt(row + dr, col + dc, ['K'])) return true;
    }

    // Pawn attacks (direction depends on attacking color)
    if (byColor === 'white') {
        if (hasEnemyAt(row + 1, col - 1, ['P']) || hasEnemyAt(row + 1, col + 1, ['P'])) return true;
    } else {
        if (hasEnemyAt(row - 1, col - 1, ['P']) || hasEnemyAt(row - 1, col + 1, ['P'])) return true;
    }

    return false;
};

export const isInCheckOnBoard = (board: Board, color: Color): boolean => {
    const kingPos = findKingPosition(board, color);
    if (!kingPos) return false;
    const opponentColor: Color = color === 'white' ? 'black' : 'white';
    return isSquareUnderAttack(board, kingPos[0], kingPos[1], opponentColor);
};

export const canCastle = (ctx: EnginePosition, color: Color, side: 'kingside' | 'queenside'): boolean => {
    const kingPos = ctx.kingPositions[color];
    if (!kingPos) return false;

    const isKingSide = side === 'kingside';
    const isStandard = !ctx.gameVariation.includes('960');

    const hasRights = isKingSide
        ? (color === 'white' ? ctx.castlingRights.whiteKingSide : ctx.castlingRights.blackKingSide)
        : (color === 'white' ? ctx.castlingRights.whiteQueenSide : ctx.castlingRights.blackQueenSide);
    if (!hasRights) return false;

    if (isInCheckOnBoard(ctx.board, color)) return false;

    const [kingRow, kingCol] = kingPos;
    const homeRow = color === 'white' ? 7 : 0;
    if (kingRow !== homeRow) return false;

    const kingOnBoard = ctx.board[kingRow][kingCol];
    if (!kingOnBoard || kingOnBoard.piece !== 'K' || kingOnBoard.color !== color) return false;

    const finalKingCol = isKingSide ? 6 : 2;
    const finalRookCol = isKingSide ? 5 : 3;

    if (isStandard) {
        // Standard Chess Castling Rules
        const expectedKingCol = 4;
        const expectedRookCol = isKingSide ? 7 : 0;
        const expectedRook = ctx.board[kingRow][expectedRookCol];
        if (
            kingCol !== expectedKingCol ||
            !expectedRook ||
            expectedRook.piece !== 'R' ||
            expectedRook.color !== color
        ) {
            return false;
        }
        // Check path between king and rook is empty
        for (let i = Math.min(kingCol, expectedRookCol) + 1; i < Math.max(kingCol, expectedRookCol); i++) {
            if (ctx.board[kingRow][i] !== null) return false;
        }

        // Check that the king doesn't travel through attacked squares
        const opponentColor = color === 'white' ? 'black' : 'white';
        const kingStart = kingCol;
        const kingEnd = finalKingCol;
        const minKing = Math.min(kingStart, kingEnd);
        const maxKing = Math.max(kingStart, kingEnd);

        for (let i = minKing; i <= maxKing; i++) {
            if (isSquareUnderAttack(ctx.board, kingRow, i, opponentColor)) {
                return false;
            }
        }
    } else {
        // Chess960 Castling Rules
        const rookPos = isKingSide
            ? (color === 'white' ? ctx.rookPositions.whiteKingSide : ctx.rookPositions.blackKingSide)
            : (color === 'white' ? ctx.rookPositions.whiteQueenSide : ctx.rookPositions.blackQueenSide);
        if (!rookPos) return false;

        const [rookRow, rookCol] = rookPos;
        if (rookRow !== kingRow) return false;

        const rookOnBoard = ctx.board[rookRow][rookCol];
        if (!rookOnBoard || rookOnBoard.piece !== 'R' || rookOnBoard.color !== color) return false;

        for (let i = Math.min(kingCol, rookCol) + 1; i < Math.max(kingCol, rookCol); i++) {
            if (ctx.board[kingRow][i] !== null) return false;
        }

        // Check squares between final king and rook positions (inclusive of final squares)
        const minFinal = Math.min(finalKingCol, finalRookCol);
        const maxFinal = Math.max(finalKingCol, finalRookCol);
        for (let i = minFinal; i <= maxFinal; i++) {
            // Skip if this square is currently occupied by the king or rook being moved
            if (i !== kingCol && i !== rookCol && ctx.board[kingRow][i] !== null) {
                return false;
            }
        }

        // Additional check: ensure all squares the king travels through are not under attack
        const opponentColor = color === 'white' ? 'black' : 'white';
        const kingStart = kingCol;
        const kingEnd = finalKingCol;
        const minKing = Math.min(kingStart, kingEnd);
        const maxKing = Math.max(kingStart, kingEnd);

        for (let i = minKing; i <= maxKing; i++) {
            if (isSquareUnderAttack(ctx.board, kingRow, i, opponentColor)) {
                return false;
            }
        }
    }

    return true;
};

export const getCastlingMoves = (ctx: EnginePosition, row: number, col: number, color: Color): Position[] => {
    const moves: Position[] = [];

    if (canCastle(ctx, color, 'kingside')) {
        const finalKingCol = 6;
        if (col !== finalKingCol) {
            moves.push([row, finalKingCol]);
        } else {
            const rookPos = color === 'white' ? ctx.rookPositions.whiteKingSide : ctx.rookPositions.blackKingSide;
            if (rookPos && rookPos[1] !== col) {
                moves.push(rookPos);
            }
        }
    }

    if (canCastle(ctx, color, 'queenside')) {
        const finalKingCol = 2;
        if (col !== finalKingCol) {
            moves.push([row, finalKingCol]);
        } else {
            const rookPos = color === 'white' ? ctx.rookPositions.whiteQueenSide : ctx.rookPositions.blackQueenSide;
            if (rookPos && rookPos[1] !== col) {
                moves.push(rookPos);
            }
        }
    }

    return moves;
};

/**
 * Raw pseudo-legal moves for a piece (no self-check filtering).
 * `includeCastling` is false during attack/escape scans to avoid recursion.
 */
export const getRawMoves = (
    ctx: EnginePosition,
    row: number,
    col: number,
    piece: PieceType,
    color: Color,
    includeCastling: boolean = true
): Position[] => {
    const moves: Position[] = [];

    const addMove = (newRow: number, newCol: number): boolean => {
        if (isValidSquare(newRow, newCol)) {
            const target = ctx.board[newRow][newCol];
            if (!target || target.color !== color) {
                moves.push([newRow, newCol]);
            }
            return !target; // Continue if square is empty
        }
        return false;
    };

    const addSlidingMoves = (directions: Position[]): void => {
        directions.forEach(([deltaRow, deltaCol]) => {
            for (let i = 1; i < 8; i++) {
                const newRow = row + deltaRow * i;
                const newCol = col + deltaCol * i;
                if (!addMove(newRow, newCol)) break;
            }
        });
    };

    const addKnightMoves = (): void => {
        KNIGHT_MOVES.forEach(([deltaRow, deltaCol]) => {
            addMove(row + deltaRow, col + deltaCol);
        });
    };

    switch (piece) {
        case 'P': {
            const direction = color === 'white' ? -1 : 1;
            const startRow = color === 'white' ? 6 : 1;

            // Forward moves
            if (isValidSquare(row + direction, col) && !ctx.board[row + direction][col]) {
                moves.push([row + direction, col]);
                if (row === startRow && !ctx.board[row + 2 * direction][col]) {
                    moves.push([row + 2 * direction, col]);
                }
            }

            // Captures
            [-1, 1].forEach(delta => {
                const newCol = col + delta;
                if (isValidSquare(row + direction, newCol)) {
                    const target = ctx.board[row + direction][newCol];
                    if (target && target.color !== color) {
                        moves.push([row + direction, newCol]);
                    }
                }
            });

            // En passant captures
            if (ctx.enPassantTarget) {
                const [enPassantRow, enPassantCol] = ctx.enPassantTarget;
                // Check if this pawn can capture en passant (pawn must be on same row as target pawn)
                const targetPawnRow = color === 'white' ? 3 : 4; // Row where target pawn should be
                if (row === targetPawnRow && Math.abs(col - enPassantCol) === 1) {
                    // The en passant capture square is the enPassantTarget position itself
                    if (isValidSquare(enPassantRow, enPassantCol)) {
                        moves.push([enPassantRow, enPassantCol]);
                    }
                }
            }
            break;
        }

        case 'R':
            addSlidingMoves(ROOK_DIRECTIONS);
            break;

        case 'B':
            addSlidingMoves(BISHOP_DIRECTIONS);
            break;

        case 'N':
            addKnightMoves();
            break;

        case 'Q':
            addSlidingMoves(QUEEN_DIRECTIONS);
            break;

        case 'K':
            KING_DIRECTIONS.forEach(([deltaRow, deltaCol]) => {
                addMove(row + deltaRow, col + deltaCol);
            });
            // Add castling moves only if explicitly requested (to avoid recursion in attack checks)
            if (includeCastling) {
                getCastlingMoves(ctx, row, col, color).forEach(([castleRow, castleCol]) => {
                    moves.push([castleRow, castleCol]);
                });

                // Also allow king-on-rook moves for intuitive castling in Chess960
                if (ctx.gameVariation.includes('960')) {
                    const rookPositionsForColor = color === 'white'
                        ? [ctx.rookPositions.whiteKingSide, ctx.rookPositions.whiteQueenSide]
                        : [ctx.rookPositions.blackKingSide, ctx.rookPositions.blackQueenSide];

                    rookPositionsForColor.forEach(rookPos => {
                        if (rookPos) {
                            const side = rookPos === (color === 'white' ? ctx.rookPositions.whiteKingSide : ctx.rookPositions.blackKingSide) ? 'kingside' : 'queenside';
                            const canCastleResult = canCastle(ctx, color, side);

                            if (canCastleResult) {
                                // Allow king to move to rook position as castling move
                                // Check if this move is not already in the moves array to avoid duplicates
                                const [rookRow, rookCol] = rookPos;
                                const alreadyExists = moves.some(([moveRow, moveCol]) => moveRow === rookRow && moveCol === rookCol);
                                if (!alreadyExists) {
                                    moves.push(rookPos);
                                }
                            }
                        }
                    });
                }
            }
            break;

        case 'H': // Hawk (Knight + Bishop)
            addKnightMoves();
            addSlidingMoves(BISHOP_DIRECTIONS);
            break;

        case 'E': // Elephant (Knight + Rook)
            addKnightMoves();
            addSlidingMoves(ROOK_DIRECTIONS);
            break;

        case 'A': // Amazon (Knight + Bishop + Rook)
            addKnightMoves();
            addSlidingMoves(QUEEN_DIRECTIONS);
            break;
    }

    return moves;
};

// ─── Castling helpers ────────────────────────────────────────────────────────

export const getCastlingSideFromMove = (
    ctx: EnginePosition,
    piece: Piece,
    fromRow: number,
    _fromCol: number,
    toRow: number,
    toCol: number
): 'kingside' | 'queenside' | null => {
    if (piece.piece !== 'K' || toRow !== fromRow) return null;

    const homeRow = piece.color === 'white' ? 7 : 0;
    if (fromRow !== homeRow) return null;

    const targetPiece = ctx.board[toRow][toCol];
    if (ctx.gameVariation.includes('960') && targetPiece?.piece === 'R' && targetPiece.color === piece.color) {
        const kingSideRook = piece.color === 'white' ? ctx.rookPositions.whiteKingSide : ctx.rookPositions.blackKingSide;
        const queenSideRook = piece.color === 'white' ? ctx.rookPositions.whiteQueenSide : ctx.rookPositions.blackQueenSide;

        if (kingSideRook && toRow === kingSideRook[0] && toCol === kingSideRook[1]) {
            return canCastle(ctx, piece.color, 'kingside') ? 'kingside' : null;
        }
        if (queenSideRook && toRow === queenSideRook[0] && toCol === queenSideRook[1]) {
            return canCastle(ctx, piece.color, 'queenside') ? 'queenside' : null;
        }
        return null;
    }

    if (toCol === 6) return canCastle(ctx, piece.color, 'kingside') ? 'kingside' : null;
    if (toCol === 2) return canCastle(ctx, piece.color, 'queenside') ? 'queenside' : null;
    return null;
};

/**
 * Detect a king-onto-rook castling shortcut and convert it to the proper
 * castling destination (the final king column).
 */
export const convertKingOnRookToCastling = (
    ctx: EnginePosition,
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
    piece: Piece
): Position => {
    if (piece.piece !== 'K') return [toRow, toCol];
    const homeRow = piece.color === 'white' ? 7 : 0;
    if (fromRow !== homeRow || toRow !== fromRow) return [toRow, toCol];

    const targetPiece = ctx.board[toRow][toCol];
    if (!targetPiece || targetPiece.piece !== 'R' || targetPiece.color !== piece.color) {
        return [toRow, toCol];
    }

    const kingPos = piece.color === 'white' ? ctx.kingPositions.white : ctx.kingPositions.black;
    if (!kingPos) return [toRow, toCol];

    const whiteKingSideRook = ctx.rookPositions.whiteKingSide;
    const whiteQueenSideRook = ctx.rookPositions.whiteQueenSide;
    const blackKingSideRook = ctx.rookPositions.blackKingSide;
    const blackQueenSideRook = ctx.rookPositions.blackQueenSide;

    let isKingSide = false;
    let isValidCastlingRook = false;

    if (piece.color === 'white') {
        if (whiteKingSideRook && toRow === whiteKingSideRook[0] && toCol === whiteKingSideRook[1]) {
            isKingSide = true;
            isValidCastlingRook = true;
        } else if (whiteQueenSideRook && toRow === whiteQueenSideRook[0] && toCol === whiteQueenSideRook[1]) {
            isKingSide = false;
            isValidCastlingRook = true;
        }
    } else {
        if (blackKingSideRook && toRow === blackKingSideRook[0] && toCol === blackKingSideRook[1]) {
            isKingSide = true;
            isValidCastlingRook = true;
        } else if (blackQueenSideRook && toRow === blackQueenSideRook[0] && toCol === blackQueenSideRook[1]) {
            isKingSide = false;
            isValidCastlingRook = true;
        }
    }

    if (!isValidCastlingRook) return [toRow, toCol];

    if (!canCastle(ctx, piece.color, isKingSide ? 'kingside' : 'queenside')) {
        return [toRow, toCol];
    }

    const finalKingCol = isKingSide ? 6 : 2;
    if (fromCol === finalKingCol) {
        return [toRow, toCol];
    }

    return [toRow, finalKingCol];
};

// ─── Move application ────────────────────────────────────────────────────────

/**
 * Execute a move including special moves like castling and en passant.
 * Pure: returns the resulting board and auxiliary state.
 */
export const applyMove = (
    ctx: EnginePosition,
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
    piece: Piece
): MoveApplicationResult => {
    const newBoard = cloneBoard(ctx.board);

    // Reset en passant target for this turn
    let newEnPassantTarget: Position | null = null;

    const castlingSide = getCastlingSideFromMove(ctx, piece, fromRow, fromCol, toRow, toCol);
    const isCastlingMove = castlingSide !== null;

    const newCastlingRights = { ...ctx.castlingRights };
    const newKingPositions = { ...ctx.kingPositions };
    const newRookPositions = { ...ctx.rookPositions };

    if (isCastlingMove) {
        const isKingSide = castlingSide === 'kingside';

        const isStandard = !ctx.gameVariation.includes('960');
        const finalKingCol = isKingSide ? 6 : 2;
        const finalRookCol = isKingSide ? 5 : 3;

        let rookFromCol: number;
        if (isStandard) {
            rookFromCol = isKingSide ? 7 : 0;
        } else {
            const rookPos = isKingSide
                ? (piece.color === 'white' ? ctx.rookPositions.whiteKingSide : ctx.rookPositions.blackKingSide)
                : (piece.color === 'white' ? ctx.rookPositions.whiteQueenSide : ctx.rookPositions.blackQueenSide);
            rookFromCol = rookPos ? rookPos[1] : -1;
        }

        if (rookFromCol !== -1) {
            const rook = newBoard[fromRow][rookFromCol];
            if (rook) {
                const kingToMove = { ...piece };
                const rookToMove = { ...rook };

                newBoard[fromRow][fromCol] = null;
                newBoard[fromRow][rookFromCol] = null;

                newBoard[fromRow][finalKingCol] = kingToMove;
                newBoard[fromRow][finalRookCol] = rookToMove;
            }
        }

        if (piece.color === 'white') {
            newCastlingRights.whiteKingSide = false;
            newCastlingRights.whiteQueenSide = false;
            newKingPositions.white = [fromRow, finalKingCol];
            if (isKingSide) {
                newRookPositions.whiteKingSide = [fromRow, finalRookCol];
            } else {
                newRookPositions.whiteQueenSide = [fromRow, finalRookCol];
            }
        } else {
            newCastlingRights.blackKingSide = false;
            newCastlingRights.blackQueenSide = false;
            newKingPositions.black = [fromRow, finalKingCol];
            if (isKingSide) {
                newRookPositions.blackKingSide = [fromRow, finalRookCol];
            } else {
                newRookPositions.blackQueenSide = [fromRow, finalRookCol];
            }
        }
    } else {
        // Check if this is an en passant capture
        const isEnPassantCapture = piece.piece === 'P' &&
            ctx.enPassantTarget &&
            toRow === ctx.enPassantTarget[0] &&
            toCol === ctx.enPassantTarget[1] &&
            Math.abs(fromCol - toCol) === 1 &&
            !newBoard[toRow][toCol];

        // Store captured rook info for later processing
        let capturedRookInfo: { color: Color; row: number; col: number } | null = null;

        if (isEnPassantCapture) {
            const capturedPawnRow = piece.color === 'white' ? toRow + 1 : toRow - 1;
            newBoard[capturedPawnRow][toCol] = null;
            newBoard[toRow][toCol] = piece;
            newBoard[fromRow][fromCol] = null;
        } else {
            const capturedPiece = newBoard[toRow][toCol];
            newBoard[toRow][toCol] = piece;
            newBoard[fromRow][fromCol] = null;

            if (capturedPiece && capturedPiece.piece === 'R') {
                capturedRookInfo = { color: capturedPiece.color, row: toRow, col: toCol };
            }
        }

        // Check if this pawn move creates an en passant opportunity
        if (piece.piece === 'P' && Math.abs(toRow - fromRow) === 2) {
            const enPassantSquareRow = fromRow + (toRow - fromRow) / 2;
            newEnPassantTarget = [enPassantSquareRow, toCol];
        }

        if (piece.piece === 'K') {
            if (piece.color === 'white') {
                newCastlingRights.whiteKingSide = false;
                newCastlingRights.whiteQueenSide = false;
                newKingPositions.white = [toRow, toCol];
            } else {
                newCastlingRights.blackKingSide = false;
                newCastlingRights.blackQueenSide = false;
                newKingPositions.black = [toRow, toCol];
            }
        } else if (piece.piece === 'R') {
            if (piece.color === 'white') {
                if (ctx.rookPositions.whiteKingSide && ctx.rookPositions.whiteKingSide[0] === fromRow && ctx.rookPositions.whiteKingSide[1] === fromCol) {
                    newCastlingRights.whiteKingSide = false;
                    newRookPositions.whiteKingSide = null;
                }
                if (ctx.rookPositions.whiteQueenSide && ctx.rookPositions.whiteQueenSide[0] === fromRow && ctx.rookPositions.whiteQueenSide[1] === fromCol) {
                    newCastlingRights.whiteQueenSide = false;
                    newRookPositions.whiteQueenSide = null;
                }
            } else {
                if (ctx.rookPositions.blackKingSide && ctx.rookPositions.blackKingSide[0] === fromRow && ctx.rookPositions.blackKingSide[1] === fromCol) {
                    newCastlingRights.blackKingSide = false;
                    newRookPositions.blackKingSide = null;
                }
                if (ctx.rookPositions.blackQueenSide && ctx.rookPositions.blackQueenSide[0] === fromRow && ctx.rookPositions.blackQueenSide[1] === fromCol) {
                    newCastlingRights.blackQueenSide = false;
                    newRookPositions.blackQueenSide = null;
                }
            }
        }

        // Process captured rook info if any
        if (capturedRookInfo) {
            if (capturedRookInfo.color === 'white') {
                if (ctx.rookPositions.whiteKingSide && ctx.rookPositions.whiteKingSide[0] === capturedRookInfo.row && ctx.rookPositions.whiteKingSide[1] === capturedRookInfo.col) {
                    newCastlingRights.whiteKingSide = false;
                    newRookPositions.whiteKingSide = null;
                }
                if (ctx.rookPositions.whiteQueenSide && ctx.rookPositions.whiteQueenSide[0] === capturedRookInfo.row && ctx.rookPositions.whiteQueenSide[1] === capturedRookInfo.col) {
                    newCastlingRights.whiteQueenSide = false;
                    newRookPositions.whiteQueenSide = null;
                }
            } else {
                if (ctx.rookPositions.blackKingSide && ctx.rookPositions.blackKingSide[0] === capturedRookInfo.row && ctx.rookPositions.blackKingSide[1] === capturedRookInfo.col) {
                    newCastlingRights.blackKingSide = false;
                    newRookPositions.blackKingSide = null;
                }
                if (ctx.rookPositions.blackQueenSide && ctx.rookPositions.blackQueenSide[0] === capturedRookInfo.row && ctx.rookPositions.blackQueenSide[1] === capturedRookInfo.col) {
                    newCastlingRights.blackQueenSide = false;
                    newRookPositions.blackQueenSide = null;
                }
            }
        }
    }

    return {
        board: newBoard,
        castlingRights: newCastlingRights,
        kingPositions: newKingPositions,
        rookPositions: newRookPositions,
        enPassantTarget: newEnPassantTarget
    };
};

export const applyPromotionChoice = (
    ctx: EnginePosition,
    square: Position,
    newPiece: PieceType,
    color: Color
): Board => {
    const newBoard = cloneBoard(ctx.board);
    newBoard[square[0]][square[1]] = { piece: newPiece, color };
    return newBoard;
};

// ─── Check validation ────────────────────────────────────────────────────────

/**
 * Check if a move would leave the moving color's king in check
 * (uses make-undo on boardOverride to avoid extra cloning).
 */
export const wouldLeaveKingInCheck = (
    ctx: EnginePosition,
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
    color: Color,
    boardOverride?: Board
): boolean => {
    const testBoard = boardOverride || cloneBoard(ctx.board);
    const movingPiece = testBoard[fromRow][fromCol];
    const castlingSide = movingPiece ? getCastlingSideFromMove(ctx, movingPiece, fromRow, fromCol, toRow, toCol) : null;

    // Save/restore stack for make-undo
    const saved: { row: number; col: number; old: BoardSquare }[] = [];
    const save = (r: number, c: number) => {
        saved.push({ row: r, col: c, old: testBoard[r][c] });
    };

    if (movingPiece && castlingSide) {
        // Simulate castling
        const isKingSide = castlingSide === 'kingside';

        const rookFromCol = isKingSide
            ? (color === 'white' ? ctx.rookPositions.whiteKingSide?.[1] : ctx.rookPositions.blackKingSide?.[1])
            : (color === 'white' ? ctx.rookPositions.whiteQueenSide?.[1] : ctx.rookPositions.blackQueenSide?.[1]);

        if (rookFromCol !== undefined) {
            const finalKingCol = isKingSide ? 6 : 2;
            const finalRookCol = isKingSide ? 5 : 3;
            const rook = testBoard[fromRow][rookFromCol];

            save(fromRow, fromCol);
            save(fromRow, rookFromCol);
            save(fromRow, finalKingCol);
            save(fromRow, finalRookCol);

            if (fromCol !== finalKingCol) {
                testBoard[fromRow][fromCol] = null;
            }
            if (rookFromCol !== finalRookCol) {
                testBoard[fromRow][rookFromCol] = null;
            }

            testBoard[fromRow][finalKingCol] = movingPiece;
            testBoard[fromRow][finalRookCol] = rook;
        }
    } else {
        // Regular move or en passant
        const isEnPassantCapture = movingPiece && movingPiece.piece === 'P' &&
            ctx.enPassantTarget &&
            toRow === ctx.enPassantTarget[0] &&
            toCol === ctx.enPassantTarget[1] &&
            Math.abs(fromCol - toCol) === 1 &&
            !testBoard[toRow][toCol];

        if (isEnPassantCapture && movingPiece) {
            const capturedPawnRow = movingPiece.color === 'white' ? toRow + 1 : toRow - 1;
            save(fromRow, fromCol);
            save(toRow, toCol);
            save(capturedPawnRow, toCol);
            testBoard[capturedPawnRow][toCol] = null;
            testBoard[toRow][toCol] = movingPiece;
            testBoard[fromRow][fromCol] = null;
        } else {
            save(fromRow, fromCol);
            save(toRow, toCol);
            testBoard[toRow][toCol] = movingPiece;
            testBoard[fromRow][fromCol] = null;
        }
    }

    const kingPos = findKingPosition(testBoard, color);
    const inCheck = !kingPos || isSquareUnderAttack(testBoard, kingPos[0], kingPos[1], color === 'white' ? 'black' : 'white');

    // Restore in reverse order
    for (let i = saved.length - 1; i >= 0; i--) {
        const { row, col, old } = saved[i];
        testBoard[row][col] = old;
    }

    return inCheck;
};

/** Legal moves for a piece (self-check filtered). */
export const getLegalMoves = (ctx: EnginePosition, row: number, col: number, piece: PieceType, color: Color): Position[] => {
    const rawMoves = getRawMoves(ctx, row, col, piece, color);
    const testBoard = cloneBoard(ctx.board);
    return rawMoves.filter(([toRow, toCol]) => {
        return !wouldLeaveKingInCheck(ctx, row, col, toRow, toCol, color, testBoard);
    });
};

/** Does the color have at least one legal move? (castling escape excluded, matching the historical engine behavior) */
export const hasAnyLegalMove = (ctx: EnginePosition, color: Color): boolean => {
    const testBoard = cloneBoard(ctx.board);
    const saved: { row: number; col: number; old: BoardSquare }[] = [];
    const save = (r: number, c: number) => {
        saved.push({ row: r, col: c, old: testBoard[r][c] });
    };

    for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
            const piece = ctx.board[row][col];
            if (!piece || piece.color !== color) continue;

            const rawMoves = getRawMoves(ctx, row, col, piece.piece, color, false);
            for (const [toRow, toCol] of rawMoves) {
                const movingPiece = testBoard[row][col];
                if (!movingPiece) continue;

                const isEnPassantCapture = movingPiece.piece === 'P' &&
                    ctx.enPassantTarget &&
                    toRow === ctx.enPassantTarget[0] &&
                    toCol === ctx.enPassantTarget[1] &&
                    Math.abs(col - toCol) === 1 &&
                    !testBoard[toRow][toCol];

                save(row, col);
                save(toRow, toCol);

                if (isEnPassantCapture) {
                    const capturedPawnRow = movingPiece.color === 'white' ? toRow + 1 : toRow - 1;
                    save(capturedPawnRow, toCol);
                    testBoard[capturedPawnRow][toCol] = null;
                }

                testBoard[toRow][toCol] = movingPiece;
                testBoard[row][col] = null;

                const kingInCheck = isInCheckOnBoard(testBoard, color);

                for (let i = saved.length - 1; i >= 0; i--) {
                    testBoard[saved[i].row][saved[i].col] = saved[i].old;
                }
                saved.length = 0;

                if (!kingInCheck) {
                    return true;
                }
            }
        }
    }
    return false;
};

export const getCheckStatusAfterMove = (
    ctx: EnginePosition,
    nextPlayerToMove: Color
): { isCheck: boolean; isCheckmate: boolean } => {
    const isCheck = isInCheckOnBoard(ctx.board, nextPlayerToMove);
    const isCheckmate = isCheck && !hasAnyLegalMove(ctx, nextPlayerToMove);
    return { isCheck, isCheckmate };
};

export const isCheckmate = (ctx: EnginePosition, color: Color): boolean => {
    if (!isInCheckOnBoard(ctx.board, color)) return false;

    for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
            const piece = ctx.board[row][col];
            if (piece && piece.color === color) {
                const moves = getLegalMoves(ctx, row, col, piece.piece, piece.color);
                if (moves.length > 0) {
                    return false;
                }
            }
        }
    }
    return true;
};

export const isStalemate = (ctx: EnginePosition, color: Color): boolean => {
    if (isInCheckOnBoard(ctx.board, color)) return false;

    for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
            const piece = ctx.board[row][col];
            if (piece && piece.color === color) {
                const moves = getLegalMoves(ctx, row, col, piece.piece, piece.color);
                if (moves.length > 0) {
                    return false;
                }
            }
        }
    }
    return true;
};

/** Board-derived game status (checkmate / stalemate / ongoing). */
export const getBoardGameStatus = (ctx: EnginePosition, currentPlayer: Color): GameState => {
    if (isCheckmate(ctx, currentPlayer)) {
        return {
            status: 'checkmate',
            winner: currentPlayer === 'white' ? 'black' : 'white'
        };
    }
    if (isStalemate(ctx, currentPlayer)) {
        return { status: 'stalemate' };
    }
    return { status: 'ongoing' };
};

// ─── Notation ────────────────────────────────────────────────────────────────

export const FIGURINE_NOTATION_SYMBOLS: Record<PieceType, string> = {
    K: '♔',
    Q: '♕',
    R: '♖',
    B: '♗',
    N: '♘',
    P: '',
    H: PIECE_SYMBOLS.white.H,
    E: PIECE_SYMBOLS.white.E,
    A: PIECE_SYMBOLS.white.A
};

export const getPieceNotation = (piece: PieceType): string => FIGURINE_NOTATION_SYMBOLS[piece];

export const generateMoveNotation = (
    from: Position,
    to: Position,
    piece: Piece,
    capturedPiece: Piece | null,
    isCastling: boolean,
    isCheck: boolean,
    isCheckmate: boolean,
    isPromotion: boolean = false,
    promotionPiece?: PieceType,
    isEnPassant: boolean = false
): string => {
    if (isCastling) {
        const [, toCol] = to;
        return toCol === 6 ? 'O-O' : 'O-O-O';
    }

    let notation = '';
    const pieceSymbol = getPieceNotation(piece.piece);
    const fromSquare = positionToAlgebraic(from);
    const toSquare = positionToAlgebraic(to);

    notation += pieceSymbol;

    if (piece.piece === 'P' && (capturedPiece || isEnPassant)) {
        notation += fromSquare[0];
    }

    if (capturedPiece || isEnPassant) {
        notation += 'x';
    }

    notation += toSquare;

    if (isPromotion && promotionPiece) {
        notation += '=' + getPieceNotation(promotionPiece);
    }

    if (isCheckmate) {
        notation += '#';
    } else if (isCheck) {
        notation += '+';
    }

    return notation;
};

/** Promotion choices available for a color, given the variant setup. */
export const getPromotionPieceTypes = (
    gameVariation: GameVariation,
    promotionRule: PromotionRule,
    playerPieces: PlayerPieces,
    color: Color
): PieceType[] => {
    const basePieces: PieceType[] = ['N', 'B', 'R'];

    if (gameVariation === 'chess' || gameVariation === 'chess960') {
        return [...basePieces, 'Q'];
    }

    if (gameVariation === 'chessx2' || gameVariation === 'chess960x2') {
        return [...basePieces, 'Q', 'H', 'E', 'A'];
    }

    if (promotionRule === 'limited') {
        const chosenPiece = playerPieces[color];
        return chosenPiece ? [...basePieces, chosenPiece] : basePieces;
    }

    return [...basePieces, 'Q', 'H', 'E', 'A'];
};

// ─── Starting positions ──────────────────────────────────────────────────────

export const STANDARD_BACK_RANK: PieceType[] = ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R'];

export interface BoardBuildResult {
    board: Board;
    kingPositions: KingPositions;
    rookPositions: RookPositions;
    castlingRights: CastlingRights;
}

const usesCustomQueenReplacement = (variation: GameVariation): boolean => (
    variation !== 'chess' && variation !== 'chessx2' && variation !== 'chess960'
);

export const buildInitialBoardFromBackRanks = (
    variation: GameVariation,
    whiteBackRank: PieceType[],
    blackBackRank: PieceType[],
    playerPieces: PlayerPieces
): BoardBuildResult => {
    const newBoard: Board = createEmptyBoard();

    let whiteKingPos: Position | null = null;
    let blackKingPos: Position | null = null;
    const whiteRooks: Position[] = [];
    const blackRooks: Position[] = [];

    for (let i = 0; i < 8; i++) {
        let whitePiece = whiteBackRank[i];
        let blackPiece = blackBackRank[i];

        // Replace queen with chosen pieces for variations that allow piece selection
        if (usesCustomQueenReplacement(variation)) {
            if (whitePiece === 'Q' && playerPieces.white) {
                whitePiece = playerPieces.white;
            }
            if (blackPiece === 'Q' && playerPieces.black) {
                blackPiece = playerPieces.black;
            }
        }

        // Track positions for castling
        if (whitePiece === 'K') {
            whiteKingPos = [7, i];
        } else if (whitePiece === 'R') {
            whiteRooks.push([7, i]);
        }

        if (blackPiece === 'K') {
            blackKingPos = [0, i];
        } else if (blackPiece === 'R') {
            blackRooks.push([0, i]);
        }

        newBoard[0][i] = { piece: blackPiece, color: 'black' };
        newBoard[7][i] = { piece: whitePiece, color: 'white' };
    }

    // In Chess960, the rook closer to the a-file is the queenside rook,
    // and the one closer to the h-file is the kingside rook.
    let whiteQueenSideRook: Position | null = null;
    let whiteKingSideRook: Position | null = null;
    let blackQueenSideRook: Position | null = null;
    let blackKingSideRook: Position | null = null;

    if (whiteRooks.length > 0) {
        whiteRooks.sort((a, b) => a[1] - b[1]); // Sort by column
        whiteQueenSideRook = whiteRooks[0]; // Leftmost is queenside
        whiteKingSideRook = whiteRooks[whiteRooks.length - 1]; // Rightmost is kingside
    }

    if (blackRooks.length > 0) {
        blackRooks.sort((a, b) => a[1] - b[1]); // Sort by column
        blackQueenSideRook = blackRooks[0]; // Leftmost is queenside
        blackKingSideRook = blackRooks[blackRooks.length - 1]; // Rightmost is kingside
    }

    // Set up pawns
    for (let i = 0; i < 8; i++) {
        newBoard[1][i] = { piece: 'P', color: 'black' };
        newBoard[6][i] = { piece: 'P', color: 'white' };
    }

    const kingPositions: KingPositions = { white: whiteKingPos, black: blackKingPos };
    const rookPositions: RookPositions = {
        whiteKingSide: whiteKingSideRook,
        whiteQueenSide: whiteQueenSideRook,
        blackKingSide: blackKingSideRook,
        blackQueenSide: blackQueenSideRook
    };
    const castlingRights: CastlingRights = {
        whiteKingSide: whiteKingSideRook !== null,
        whiteQueenSide: whiteQueenSideRook !== null,
        blackKingSide: blackKingSideRook !== null,
        blackQueenSide: blackQueenSideRook !== null
    };

    return { board: newBoard, kingPositions, rookPositions, castlingRights };
};

/** Symmetric starting position (same arrangement for both colors). */
export const buildInitialBoardFromBackRank = (
    variation: GameVariation,
    backRank: PieceType[],
    playerPieces: PlayerPieces
): BoardBuildResult => buildInitialBoardFromBackRanks(variation, backRank, backRank, playerPieces);

// ─── Blind Chess960 back-rank setup ─────────────────────────────────────────

/**
 * Parse an 8-character back-rank string ("RNBQKBNR") into pieces.
 * Returns null when the string is not exactly 8 valid non-pawn pieces.
 */
export const parseBackRank = (value: string): PieceType[] | null => {
    if (typeof value !== 'string' || value.length !== 8) return null;
    const pieces: PieceType[] = [];
    for (const ch of value) {
        if (!VALID_PIECE_TYPES.includes(ch as PieceType) || ch === 'P') return null;
        pieces.push(ch as PieceType);
    }
    return pieces;
};

/**
 * Validate a Blind Chess960 back rank: the exact standard piece multiset with
 * the major piece (queen or its fairy replacement) in the queen slot, bishops
 * on opposite-colored squares, and the king between the two rooks.
 * Returns a human-readable problem description, or null when valid.
 */
export const validateBlindBackRank = (backRank: string, majorPiece: PieceType): string | null => {
    if (!['Q', 'H', 'E', 'A'].includes(majorPiece)) {
        return 'The major piece must be Queen, Hawk, Elephant, or Amazon.';
    }

    const rank = parseBackRank(backRank);
    if (!rank) {
        return 'Back rank must be exactly 8 pieces (K, R, R, B, B, N, N plus the major piece).';
    }

    const counts = new Map<PieceType, number>();
    for (const piece of rank) {
        counts.set(piece, (counts.get(piece) ?? 0) + 1);
    }

    const requiredCounts: Array<[PieceType, number]> = [['K', 1], ['R', 2], ['B', 2], ['N', 2]];
    for (const [piece, count] of requiredCounts) {
        if (counts.get(piece) !== count) {
            const expected = count === 1 ? 'exactly one' : 'exactly two';
            return `Back rank must contain ${expected} ${PIECE_NAMES[piece].toLowerCase()}${count === 1 ? '' : 's'}.`;
        }
    }

    if (counts.get(majorPiece) !== 1) {
        return `Back rank must contain exactly one ${PIECE_NAMES[majorPiece].toLowerCase()}.`;
    }

    const bishopCols: number[] = [];
    const rookCols: number[] = [];
    let kingCol = -1;
    rank.forEach((piece, col) => {
        if (piece === 'B') bishopCols.push(col);
        if (piece === 'R') rookCols.push(col);
        if (piece === 'K') kingCol = col;
    });

    if ((bishopCols[0] % 2) === (bishopCols[1] % 2)) {
        return 'Bishops must start on opposite-colored squares.';
    }
    if (!(rookCols[0] < kingCol && kingCol < rookCols[1])) {
        return 'The king must start between the two rooks.';
    }

    return null;
};

/**
 * Build the initial board for a known starting position.
 * Returns null when the variation is a Chess960 variant and no position id
 * was supplied (the client then picks a random position locally).
 */
export const buildInitialBoard = (
    variation: GameVariation,
    playerPieces: PlayerPieces,
    chess960PositionId: number | null
): BoardBuildResult | null => {
    let baseRank: PieceType[] | null;
    if (variation.includes('960')) {
        baseRank = chess960PositionId !== null && chess960PositionId !== undefined
            ? chess960PositionFromId(chess960PositionId)
            : null;
    } else {
        baseRank = STANDARD_BACK_RANK;
    }

    if (!baseRank) return null;

    return buildInitialBoardFromBackRank(variation, baseRank, playerPieces);
};

// ─── Aux-state derivation ────────────────────────────────────────────────────

/**
 * Derive king/rook positions and castling rights from a board.
 * Matches how the client tracks these values at the start of a game
 * (kings and rooks on their home ranks; leftmost rook is queenside).
 */
export const deriveAuxFromBoard = (board: Board): { kingPositions: KingPositions; rookPositions: RookPositions; castlingRights: CastlingRights } => {
    const kingPositions: KingPositions = { white: null, black: null };
    const rookPositions: RookPositions = createEmptyRookPositions();
    const castlingRights: CastlingRights = createEmptyCastlingRights();

    (['white', 'black'] as const).forEach(color => {
        const homeRow = color === 'white' ? 7 : 0;
        const rooks: Position[] = [];

        for (let col = 0; col < 8; col++) {
            const square = board[homeRow][col];
            if (!square || square.color !== color) continue;
            if (square.piece === 'K') {
                kingPositions[color] = [homeRow, col];
            } else if (square.piece === 'R') {
                rooks.push([homeRow, col]);
            }
        }

        rooks.sort((a, b) => a[1] - b[1]);
        if (rooks.length > 0) {
            rookPositions[color === 'white' ? 'whiteQueenSide' : 'blackQueenSide'] = rooks[0];
            rookPositions[color === 'white' ? 'whiteKingSide' : 'blackKingSide'] = rooks[rooks.length - 1];
        }

        if (color === 'white') {
            castlingRights.whiteKingSide = rookPositions.whiteKingSide !== null;
            castlingRights.whiteQueenSide = rookPositions.whiteQueenSide !== null;
        } else {
            castlingRights.blackKingSide = rookPositions.blackKingSide !== null;
            castlingRights.blackQueenSide = rookPositions.blackQueenSide !== null;
        }
    });

    return { kingPositions, rookPositions, castlingRights };
};

// ─── Snapshot validation (server side) ───────────────────────────────────────

const isPosition = (value: unknown): value is Position =>
    Array.isArray(value) && value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number';

const isPiece = (value: unknown): value is Piece =>
    typeof value === 'object' && value !== null &&
    VALID_PIECE_TYPES.includes((value as Record<string, unknown>).piece as PieceType) &&
    VALID_COLORS.includes((value as Record<string, unknown>).color as Color);

/**
 * Structural validation of an incoming snapshot. Returns a human-readable
 * problem description, or null when the snapshot is well-formed.
 */
export const validateSnapshotShape = (snapshot: unknown): string | null => {
    if (typeof snapshot !== 'object' || snapshot === null) return 'Snapshot must be an object.';
    const snap = snapshot as OnlineGameSnapshot;

    if (typeof snap.version !== 'number' || !Number.isFinite(snap.version) || snap.version < 0) {
        return 'Snapshot version must be a non-negative number.';
    }
    if (!VALID_VARIATIONS.includes(snap.gameVariation)) return 'Unknown game variation.';
    if (!VALID_PROMOTION_RULES.includes(snap.promotionRule)) return 'Unknown promotion rule.';
    if (!snap.playerPieces || typeof snap.playerPieces !== 'object' ||
        !isMajorPiece(snap.playerPieces.white) || !isMajorPiece(snap.playerPieces.black)) {
        return 'Invalid player pieces.';
    }
    if (!['playing', 'promotion', 'setup'].includes(snap.gamePhase)) return 'Invalid game phase.';
    if (!VALID_COLORS.includes(snap.currentPlayer)) return 'Invalid current player.';

    if (!Array.isArray(snap.board) || snap.board.length !== 8) return 'Board must have 8 rows.';
    for (const row of snap.board) {
        if (!Array.isArray(row) || row.length !== 8) return 'Each board row must have 8 columns.';
        for (const cell of row) {
            if (cell !== null && !isPiece(cell)) return 'Invalid piece on the board.';
        }
    }

    if (!Array.isArray(snap.gameHistory)) return 'Game history must be an array.';
    for (const move of snap.gameHistory) {
        if (!move || !isPosition(move.from) || !isPosition(move.to) || !isPiece(move.piece)) {
            return 'Invalid move entry in history.';
        }
        if (!isValidSquare(move.from[0], move.from[1]) || !isValidSquare(move.to[0], move.to[1])) {
            return 'Move coordinates out of bounds.';
        }
    }

    if (snap.promotionSquare !== null && !isInBoundsPosition(snap.promotionSquare)) return 'Invalid promotion square.';
    if (snap.enPassantTarget !== null && !isInBoundsPosition(snap.enPassantTarget)) return 'Invalid en passant target.';
    if (typeof snap.castlingRights !== 'object' || snap.castlingRights === null) return 'Invalid castling rights.';
    if (typeof snap.kingPositions !== 'object' || snap.kingPositions === null) return 'Invalid king positions.';
    if (typeof snap.rookPositions !== 'object' || snap.rookPositions === null) return 'Invalid rook positions.';
    if (snap.resignedBy !== null && snap.resignedBy !== undefined && !VALID_COLORS.includes(snap.resignedBy)) return 'Invalid resignedBy.';
    if (snap.drawOfferedBy !== null && snap.drawOfferedBy !== undefined && !VALID_COLORS.includes(snap.drawOfferedBy)) return 'Invalid drawOfferedBy.';
    if (snap.timeoutWinner !== null && snap.timeoutWinner !== undefined && !VALID_COLORS.includes(snap.timeoutWinner)) return 'Invalid timeoutWinner.';
    if (snap.whiteTimeMs !== undefined && (typeof snap.whiteTimeMs !== 'number' || snap.whiteTimeMs < 0)) return 'Invalid white clock.';
    if (snap.blackTimeMs !== undefined && (typeof snap.blackTimeMs !== 'number' || snap.blackTimeMs < 0)) return 'Invalid black clock.';

    return null;
};

const isMajorPiece = (value: unknown): value is PieceType | null =>
    value === null || VALID_PIECE_TYPES.includes(value as PieceType);

const isInBoundsPosition = (value: unknown): value is Position =>
    isPosition(value) && isValidSquare(value[0], value[1]);

const boardsEqual = (left: Board, right: Board): boolean => JSON.stringify(left) === JSON.stringify(right);
const jsonEqual = (left: unknown, right: unknown): boolean => JSON.stringify(left) === JSON.stringify(right);
const samePosition = (left: Position, right: Position): boolean => left[0] === right[0] && left[1] === right[1];
const sameMoveCoords = (left: GameMove, right: GameMove): boolean =>
    samePosition(left.from, right.from) && samePosition(left.to, right.to);

const positionalStateEquals = (prev: OnlineGameSnapshot, next: OnlineGameSnapshot): boolean => {
    if (!boardsEqual(prev.board, next.board)) return false;
    if (prev.gamePhase !== next.gamePhase) return false;
    if (prev.currentPlayer !== next.currentPlayer) return false;
    if (!jsonEqual(prev.promotionSquare, next.promotionSquare)) return false;
    if (!jsonEqual(prev.enPassantTarget, next.enPassantTarget)) return false;
    if (!jsonEqual(prev.castlingRights, next.castlingRights)) return false;
    if (!jsonEqual(prev.kingPositions, next.kingPositions)) return false;
    if (!jsonEqual(prev.rookPositions, next.rookPositions)) return false;
    if (prev.gameVariation !== next.gameVariation) return false;
    if (!jsonEqual(prev.playerPieces, next.playerPieces)) return false;
    if (prev.promotionRule !== next.promotionRule) return false;

    // Histories may differ in length because the server stores only the last
    // move in KV; only the tail of the histories must agree.
    if (prev.gameHistory.length === 0) {
        if (next.gameHistory.length !== 0) return false;
    } else {
        const nextLast = next.gameHistory[next.gameHistory.length - 1];
        const prevLast = prev.gameHistory[prev.gameHistory.length - 1];
        if (!nextLast || !sameMoveCoords(prevLast, nextLast)) return false;
    }
    return true;
};

export interface SnapshotTransitionResult {
    ok: boolean;
    reason: string | null;
    /** The color that must have authored this change, or null for anyone. */
    mover: Color | null;
}

const transitionOk = (mover: Color | null): SnapshotTransitionResult => ({ ok: true, reason: null, mover });
const transitionFail = (reason: string): SnapshotTransitionResult => ({ ok: false, reason, mover: null });

/**
 * Validate that `next` is a legal successor of the authoritative `prev`
 * snapshot: either an unchanged position with legal metadata changes
 * (clocks, resignation, draw agreement) or exactly one legal move /
 * promotion completion. Returns the color that must have sent it.
 */
export const validateSnapshotTransition = (
    prev: OnlineGameSnapshot,
    next: OnlineGameSnapshot,
    senderRole: Color | null
): SnapshotTransitionResult => {
    const shapeError = validateSnapshotShape(next);
    if (shapeError) return transitionFail(shapeError);

    // ── Metadata-only update (no positional change) ──────────────────────────
    if (positionalStateEquals(prev, next)) {
        if (next.drawAgreed && !prev.drawAgreed && !prev.drawOfferedBy) {
            return transitionFail('Draw cannot be agreed without a prior offer.');
        }
        if (next.drawAgreed === false && prev.drawAgreed === true) {
            return transitionFail('Draw agreement cannot be revoked.');
        }
        if (!next.drawAgreed && next.drawOfferedBy !== prev.drawOfferedBy) {
            return transitionFail('Draw offers must accompany a move.');
        }
        if (prev.resignedBy && next.resignedBy && prev.resignedBy !== next.resignedBy) {
            return transitionFail('Resigning player cannot change.');
        }
        if (prev.resignedBy && next.resignedBy === null) {
            return transitionFail('Resignation cannot be revoked.');
        }

        // Timeout propagation: the winner is the side that did NOT run out of
        // time, i.e. the opposite of the side to move. Either participant may
        // relay this result (both clients detect the timeout independently).
        const prevTimeout = prev.timeoutWinner ?? null;
        const nextTimeout = next.timeoutWinner ?? null;
        if (nextTimeout !== prevTimeout) {
            if (nextTimeout === null) {
                return transitionFail('Timeout result cannot be revoked.');
            }
            if (prevTimeout !== null) {
                return transitionFail('Timeout winner cannot change.');
            }
            if (prev.resignedBy || prev.drawAgreed) {
                return transitionFail('Game has already ended.');
            }
            if (prev.gamePhase !== 'playing') {
                return transitionFail('Timeout can only end a playing game.');
            }
            if (nextTimeout === prev.currentPlayer) {
                return transitionFail('Timeout winner must be the side that did not run out of time.');
            }
        }

        return transitionOk(null);
    }

    // ── New move (playing → playing or playing → promotion) ──────────────────
    if (prev.gamePhase === 'playing' && (next.gamePhase === 'playing' || next.gamePhase === 'promotion')) {
        if ((next.timeoutWinner ?? null) !== (prev.timeoutWinner ?? null)) {
            return transitionFail('Timeout result cannot accompany a move.');
        }

        const lastMove = next.gameHistory[next.gameHistory.length - 1];
        if (!lastMove) {
            return transitionFail('Board changed without a recorded move.');
        }

        // The new move must be the tail of the client history and must extend
        // the server's stored history (which keeps only the last move).
        if (prev.gameHistory.length > 0) {
            const penultimate = next.gameHistory[next.gameHistory.length - 2];
            const prevLast = prev.gameHistory[prev.gameHistory.length - 1];
            if (!penultimate || !sameMoveCoords(prevLast, penultimate)) {
                return transitionFail('Move history does not extend the previous state.');
            }
        } else if (next.gameHistory.length !== 1) {
            return transitionFail('Move history does not extend the previous state.');
        }

        const moverColor = prev.currentPlayer;
        if (senderRole !== null && senderRole !== moverColor) {
            return transitionFail('It is not your turn.');
        }
        if (lastMove.piece.color !== moverColor) {
            return transitionFail('The recorded move was made by the wrong color.');
        }

        const [fromRow, fromCol] = lastMove.from;
        const [toRow, toCol] = lastMove.to;
        const ctx = positionFromSnapshot(prev);

        const pieceAtFrom = ctx.board[fromRow][fromCol];
        if (!pieceAtFrom || pieceAtFrom.piece !== lastMove.piece.piece || pieceAtFrom.color !== moverColor) {
            return transitionFail('Recorded move does not match the piece on the board.');
        }

        const legalMoves = getLegalMoves(ctx, fromRow, fromCol, pieceAtFrom.piece, moverColor);
        if (!legalMoves.some(([moveRow, moveCol]) => moveRow === toRow && moveCol === toCol)) {
            return transitionFail('Illegal move rejected.');
        }

        const applied = applyMove(ctx, fromRow, fromCol, toRow, toCol, pieceAtFrom);
        if (!boardsEqual(applied.board, next.board)) {
            return transitionFail('Board does not match the recorded move.');
        }
        if (!jsonEqual(applied.castlingRights, next.castlingRights)) return transitionFail('Castling rights mismatch.');
        if (!jsonEqual(applied.kingPositions, next.kingPositions)) return transitionFail('King position mismatch.');
        if (!jsonEqual(applied.rookPositions, next.rookPositions)) return transitionFail('Rook position mismatch.');
        if (!jsonEqual(applied.enPassantTarget, next.enPassantTarget)) return transitionFail('En passant target mismatch.');

        if (next.gamePhase === 'promotion') {
            const promotionRank = moverColor === 'white' ? 0 : 7;
            if (lastMove.piece.piece !== 'P' || toRow !== promotionRank) {
                return transitionFail('Promotion phase requires a pawn reaching the last rank.');
            }
            if (!jsonEqual(next.promotionSquare, [toRow, toCol])) {
                return transitionFail('Promotion square does not match the move.');
            }
            if (next.currentPlayer !== prev.currentPlayer) {
                return transitionFail('Promotion must keep the moving player to move.');
            }
        } else {
            if (next.promotionSquare !== null) {
                return transitionFail('Unexpected promotion square.');
            }
            const expectedNextPlayer: Color = moverColor === 'white' ? 'black' : 'white';
            if (next.currentPlayer !== expectedNextPlayer) {
                return transitionFail('Turn did not switch after the move.');
            }
        }

        return transitionOk(moverColor);
    }

    // ── Promotion completion (promotion → playing) ───────────────────────────
    if (prev.gamePhase === 'promotion' && next.gamePhase === 'playing') {
        if ((next.timeoutWinner ?? null) !== (prev.timeoutWinner ?? null)) {
            return transitionFail('Timeout result cannot accompany a promotion.');
        }

        const square = prev.promotionSquare;
        if (!square) {
            return transitionFail('Promotion square missing from previous state.');
        }
        if (next.promotionSquare !== null) {
            return transitionFail('Promotion square must clear after promotion.');
        }

        const moverColor = prev.currentPlayer;
        if (senderRole !== null && senderRole !== moverColor) {
            return transitionFail('It is not your turn.');
        }

        const ctx = positionFromSnapshot(prev);
        const pawn = ctx.board[square[0]][square[1]];
        if (!pawn || pawn.piece !== 'P' || pawn.color !== moverColor) {
            return transitionFail('No pawn found on the promotion square.');
        }

        const promoted = next.board[square[0]][square[1]];
        const allowedPieces = getPromotionPieceTypes(prev.gameVariation, prev.promotionRule, prev.playerPieces, moverColor);
        if (!promoted || promoted.color !== moverColor || !allowedPieces.includes(promoted.piece)) {
            return transitionFail('Promotion piece is not allowed in this variant.');
        }

        const expectedBoard = applyPromotionChoice(ctx, square, promoted.piece, moverColor);
        if (!boardsEqual(expectedBoard, next.board)) {
            return transitionFail('Board does not match the promotion.');
        }

        const expectedNextPlayer: Color = moverColor === 'white' ? 'black' : 'white';
        if (next.currentPlayer !== expectedNextPlayer) {
            return transitionFail('Turn did not switch after the promotion.');
        }
        if (!jsonEqual(prev.castlingRights, next.castlingRights)) return transitionFail('Castling rights changed during promotion.');
        if (!jsonEqual(prev.kingPositions, next.kingPositions)) return transitionFail('King positions changed during promotion.');
        if (!jsonEqual(prev.rookPositions, next.rookPositions)) return transitionFail('Rook positions changed during promotion.');
        if (!jsonEqual(prev.enPassantTarget, next.enPassantTarget)) return transitionFail('En passant target changed during promotion.');
        if (prev.gameHistory.length > 0) {
            const nextLast = next.gameHistory[next.gameHistory.length - 1];
            const prevLast = prev.gameHistory[prev.gameHistory.length - 1];
            if (!nextLast || !sameMoveCoords(prevLast, nextLast)) {
                return transitionFail('Move history changed during promotion.');
            }
        }

        return transitionOk(moverColor);
    }

    return transitionFail('Unsupported game state transition.');
};

const samePieceCounts = (rank: BoardSquare[], expected: PieceType[]): boolean => {
    const counts = new Map<PieceType, number>();
    for (const square of rank) {
        if (!square) return false;
        counts.set(square.piece, (counts.get(square.piece) ?? 0) + 1);
    }
    const expectedCounts = new Map<PieceType, number>();
    for (const piece of expected) {
        expectedCounts.set(piece, (expectedCounts.get(piece) ?? 0) + 1);
    }
    if (counts.size !== expectedCounts.size) return false;
    for (const [piece, count] of expectedCounts) {
        if (counts.get(piece) !== count) return false;
    }
    return true;
};

/** Expected home-rank pieces for a variation (standard set with queen replacement). */
const expectedBackRank = (variation: GameVariation, playerPieces: PlayerPieces, color: Color): PieceType[] => {
    const rank = [...STANDARD_BACK_RANK];
    if (usesCustomQueenReplacement(variation)) {
        const replacement = playerPieces[color];
        if (replacement) {
            return rank.map(piece => piece === 'Q' ? replacement : piece);
        }
    }
    return rank;
};

/**
 * Structural validation of an initial board whose exact starting position is
 * unknown to the server (random Chess960): pawns in place, correct home-rank
 * piece sets, bishops on opposite colors, king between rooks, and consistent
 * auxiliary state.
 */
export const validateInitialBoardStructure = (snapshot: OnlineGameSnapshot): string | null => {
    const board = snapshot.board;

    for (let col = 0; col < 8; col++) {
        const whitePawn = board[6][col];
        const blackPawn = board[1][col];
        if (!whitePawn || whitePawn.piece !== 'P' || whitePawn.color !== 'white') {
            return 'Initial position must have white pawns on the second rank.';
        }
        if (!blackPawn || blackPawn.piece !== 'P' || blackPawn.color !== 'black') {
            return 'Initial position must have black pawns on the seventh rank.';
        }
    }

    for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
            if ((row >= 2 && row <= 5) && board[row][col] !== null) {
                return 'Initial position must have empty center ranks.';
            }
        }
    }

    const whiteRank = board[7];
    const blackRank = board[0];
    if (whiteRank.some(square => !square || square.color !== 'white')) {
        return 'White back rank must be fully occupied by white pieces.';
    }
    if (blackRank.some(square => !square || square.color !== 'black')) {
        return 'Black back rank must be fully occupied by black pieces.';
    }
    if (!samePieceCounts(whiteRank, expectedBackRank(snapshot.gameVariation, snapshot.playerPieces, 'white'))) {
        return 'White back rank has an unexpected set of pieces.';
    }
    if (!samePieceCounts(blackRank, expectedBackRank(snapshot.gameVariation, snapshot.playerPieces, 'black'))) {
        return 'Black back rank has an unexpected set of pieces.';
    }

    for (const [rank, color] of [[whiteRank, 'white'], [blackRank, 'black']] as const) {
        const bishopCols: number[] = [];
        const rookCols: number[] = [];
        let kingCol = -1;
        rank.forEach((square, col) => {
            if (square && square.piece === 'B') bishopCols.push(col);
            if (square && square.piece === 'R') rookCols.push(col);
            if (square && square.piece === 'K') kingCol = col;
        });

        if (bishopCols.length !== 2 || (bishopCols[0] % 2) === (bishopCols[1] % 2)) {
            return `${color === 'white' ? 'White' : 'Black'} bishops must start on opposite colors.`;
        }
        if (kingCol === -1 || rookCols.length < 2) {
            return `${color === 'white' ? 'White' : 'Black'} must start with a king and two rooks.`;
        }
        if (!(rookCols[0] < kingCol && kingCol < rookCols[1])) {
            return `${color === 'white' ? 'White' : 'Black'} king must start between the rooks.`;
        }
    }

    const derived = deriveAuxFromBoard(board);
    if (!jsonEqual(snapshot.kingPositions, derived.kingPositions)) return 'King position bookkeeping mismatch.';
    if (!jsonEqual(snapshot.rookPositions, derived.rookPositions)) return 'Rook position bookkeeping mismatch.';
    if (!jsonEqual(snapshot.castlingRights, derived.castlingRights)) return 'Castling rights mismatch.';

    return null;
};

/**
 * Validate the FIRST snapshot of a room: the starting position must match the
 * room configuration (when the position is known to the server) or at least be
 * a structurally valid variant start (random Chess960). A recorded move history
 * is replayed and must end on exactly the submitted board.
 */
export const validateFirstSnapshot = (snapshot: OnlineGameSnapshot, config: OnlineRoomConfig): string | null => {
    if (config.gameVariation === 'chess960blind') {
        return 'Blind Chess960 games are started by the server once both setups are submitted.';
    }
    if (snapshot.gamePhase !== 'playing' && snapshot.gamePhase !== 'promotion') {
        return 'A game snapshot must be in the playing phase.';
    }
    if (snapshot.gameVariation !== config.gameVariation) {
        return 'Snapshot variation does not match the room configuration.';
    }
    if (!jsonEqual(snapshot.playerPieces, config.playerPieces)) {
        return 'Snapshot pieces do not match the room configuration.';
    }
    if (snapshot.promotionRule !== config.promotionRule) {
        return 'Snapshot promotion rule does not match the room configuration.';
    }

    const built = buildInitialBoard(config.gameVariation, config.playerPieces, config.chess960PositionId ?? null);

    let ctx: EnginePosition;
    if (built) {
        ctx = {
            board: built.board,
            gameVariation: config.gameVariation,
            enPassantTarget: null,
            castlingRights: built.castlingRights,
            kingPositions: built.kingPositions,
            rookPositions: built.rookPositions
        };
    } else if (snapshot.gameHistory.length === 0) {
        // Random Chess960: the server does not know the position, so verify
        // the board structure instead of an exact match.
        const structuralError = validateInitialBoardStructure(snapshot);
        if (structuralError) return structuralError;
        ctx = positionFromSnapshot(snapshot);
        ctx.enPassantTarget = null;
    } else {
        // Random Chess960 with moves already recorded: the starting position
        // cannot be reconstructed, so this snapshot is accepted as the
        // authoritative baseline.
        return null;
    }

    // Replay the recorded history; every move must be legal from the start.
    for (const move of snapshot.gameHistory) {
        const [fromRow, fromCol] = move.from;
        const [toRow, toCol] = move.to;
        const pieceAtFrom = ctx.board[fromRow][fromCol];
        if (!pieceAtFrom || pieceAtFrom.piece !== move.piece.piece || pieceAtFrom.color !== move.piece.color) {
            return 'Replay failed: recorded move does not match the board.';
        }
        const legalMoves = getLegalMoves(ctx, fromRow, fromCol, pieceAtFrom.piece, pieceAtFrom.color);
        if (!legalMoves.some(([moveRow, moveCol]) => moveRow === toRow && moveCol === toCol)) {
            return 'Replay failed: recorded move is not legal.';
        }
        const applied = applyMove(ctx, fromRow, fromCol, toRow, toCol, pieceAtFrom);
        ctx = { ...ctx, ...applied };
    }

    if (!boardsEqual(ctx.board, snapshot.board)) {
        return 'Board does not match the recorded move history.';
    }
    if (!jsonEqual(ctx.castlingRights, snapshot.castlingRights)) return 'Castling rights mismatch.';
    if (!jsonEqual(ctx.kingPositions, snapshot.kingPositions)) return 'King position bookkeeping mismatch.';
    if (!jsonEqual(ctx.rookPositions, snapshot.rookPositions)) return 'Rook position bookkeeping mismatch.';
    if (!jsonEqual(ctx.enPassantTarget, snapshot.enPassantTarget)) return 'En passant bookkeeping mismatch.';

    if (snapshot.gameHistory.length === 0) {
        if (snapshot.currentPlayer !== 'white') return 'White must move first.';
        if (snapshot.promotionSquare !== null) return 'Unexpected promotion square.';
    } else {
        const lastMove = snapshot.gameHistory[snapshot.gameHistory.length - 1];
        if (snapshot.gamePhase === 'promotion') {
            if (snapshot.currentPlayer !== lastMove.piece.color) {
                return 'Promotion phase must keep the moving player to move.';
            }
            if (!jsonEqual(snapshot.promotionSquare, lastMove.to)) {
                return 'Promotion square does not match the last move.';
            }
        } else {
            if (snapshot.currentPlayer === lastMove.piece.color) {
                return 'Turn must switch after the last move.';
            }
            if (snapshot.promotionSquare !== null) {
                return 'Unexpected promotion square.';
            }
        }
    }

    return null;
};
